/**
 * Context Gatherer - Collects comprehensive design system context for LLM analysis
 */
import fs from 'fs/promises';
import path from 'path';

export interface DesignSystemContext {
  tokens: {
    space: Record<string, string>;
    colors: Record<string, any>;
    typography: Record<string, any>;
    borders?: Record<string, any>;
    shadows?: Record<string, any>;
  };
  components: ComponentContext[];
  sections: SectionContext[];
  views: ViewContext[];
  currentStory?: StoryContext;
  projectStructure: ProjectStructure;
}

export interface ComponentContext {
  name: string;
  filePath: string;
  props: Record<string, any>;
  variants: string[];
  styling: {
    classes: string[];
    tokenUsage: string[];
  };
  storybook: {
    stories: string[];
    variants: string[];
  };
  accessibility: {
    ariaSupport: boolean;
    keyboardNav: boolean;
    contrastRatio?: string;
  };
}

export interface SectionContext {
  id: string;
  name: string;
  description: string;
  variants: Array<{
    name: string;
    description: string;
    presentation: boolean;
  }>;
  dependencies: string[];
}

export interface ViewContext {
  id: string;
  name: string;
  layout: {
    type: string;
    cols?: number;
    gap: string;
  };
  sections: Array<{
    use: string;
    variant: string;
    grid?: any;
  }>;
}

export interface StoryContext {
  component: string;
  story: string;
  props: Record<string, any>;
  visual: {
    backgroundColor?: string;
    textColor?: string;
    borderRadius?: string;
    padding?: string;
    fontSize?: string;
  };
  tokens: string[];
}

export interface ProjectStructure {
  hasTokens: boolean;
  hasComponents: boolean;
  hasSections: boolean;
  hasViews: boolean;
  hasStorybook: boolean;
  componentCount: number;
  sectionCount: number;
}

export class ContextGatherer {
  private projectPath: string;

  constructor(projectPath: string) {
    this.projectPath = projectPath;
  }

  /**
   * Gather complete design system context
   */
  async gatherContext(): Promise<DesignSystemContext> {
    const [
      tokens,
      components,
      sections,
      views,
      projectStructure
    ] = await Promise.all([
      this.gatherTokens(),
      this.gatherComponents(),
      this.gatherSections(),
      this.gatherViews(),
      this.analyzeProjectStructure()
    ]);

    return {
      tokens,
      components,
      sections,
      views,
      projectStructure
    };
  }

  /**
   * Gather all design tokens
   */
  private async gatherTokens() {
    const tokens: any = {
      space: {},
      colors: {},
      typography: {}
    };

    try {
      // Check for tokens in current project
      const tokensPath = path.join(this.projectPath, 'src/tokens');
      
      // Try to read individual token files
      const tokenFiles = ['colors.ts', 'spacing.ts', 'typography.ts', 'borders.ts', 'shadows.ts'];
      
      for (const file of tokenFiles) {
        try {
          const filePath = path.join(tokensPath, file);
          const content = await fs.readFile(filePath, 'utf-8');
          
          // Extract token values (simplified parsing)
          const tokenName = file.replace('.ts', '');
          tokens[tokenName] = this.parseTokenFile(content);
        } catch {
          // File doesn't exist, skip
        }
      }

      // Fallback to global tokens
      try {
        const globalTokensPath = path.join(this.projectPath, '../../../tokens/tokens.json');
        const globalTokens = JSON.parse(await fs.readFile(globalTokensPath, 'utf-8'));
        
        // Merge with any project-specific tokens
        Object.assign(tokens, globalTokens);
      } catch {
        // No global tokens
      }

    } catch (error) {
      console.warn('Failed to gather tokens:', error);
    }

    return tokens;
  }

  /**
   * Gather component information
   */
  private async gatherComponents(): Promise<ComponentContext[]> {
    const components: ComponentContext[] = [];

    try {
      const componentsPath = path.join(this.projectPath, 'src/components');
      const componentFiles = await fs.readdir(componentsPath);

      for (const file of componentFiles) {
        if (file.endsWith('.tsx')) {
          const componentName = file.replace('.tsx', '');
          const filePath = path.join(componentsPath, file);
          
          const component = await this.analyzeComponent(componentName, filePath);
          if (component) {
            components.push(component);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to gather components:', error);
    }

    return components;
  }

  /**
   * Analyze individual component
   */
  private async analyzeComponent(name: string, filePath: string): Promise<ComponentContext | null> {
    try {
      const content = await fs.readFile(filePath, 'utf-8');
      
      // Extract props interface (simplified)
      const propsMatch = content.match(/interface\\s+\\w*Props\\s*\\{([^}]+)\\}/);
      const props = propsMatch ? this.parsePropsInterface(propsMatch[1]) : {};

      // Extract variants from props or CSS classes
      const variants = this.extractVariants(content);

      // Check for CSS file
      const cssPath = filePath.replace('.tsx', '.css');
      let styling: { classes: string[], tokenUsage: string[] } = { classes: [], tokenUsage: [] };
      try {
        const cssContent = await fs.readFile(cssPath, 'utf-8');
        styling = this.analyzeCSS(cssContent);
      } catch {
        // No CSS file
      }

      // Check for stories
      const storiesPath = filePath.replace('.tsx', '.stories.ts');
      let storybook: { stories: string[], variants: string[] } = { stories: [], variants: [] };
      try {
        const storiesContent = await fs.readFile(storiesPath, 'utf-8');
        storybook = this.analyzeStories(storiesContent);
      } catch {
        // No stories file
      }

      return {
        name,
        filePath,
        props,
        variants,
        styling,
        storybook,
        accessibility: {
          ariaSupport: content.includes('aria-'),
          keyboardNav: content.includes('onKeyDown') || content.includes('tabIndex'),
          contrastRatio: this.calculateContrastRatio(styling.tokenUsage)
        }
      };
    } catch (error) {
      console.warn(`Failed to analyze component ${name}:`, error);
      return null;
    }
  }

  /**
   * Gather section information from manifests
   */
  private async gatherSections(): Promise<SectionContext[]> {
    const sections: SectionContext[] = [];

    try {
      // Check for sections in packages/sections
      const sectionsPath = path.join(this.projectPath, '../../sections');
      const sectionDirs = await fs.readdir(sectionsPath);

      for (const dir of sectionDirs) {
        if (dir === 'node_modules' || dir.includes('.')) continue;

        try {
          const manifestPath = path.join(sectionsPath, dir, 'manifest.json');
          const manifest = JSON.parse(await fs.readFile(manifestPath, 'utf-8'));
          
          sections.push({
            id: manifest.id,
            name: manifest.name,
            description: manifest.description,
            variants: manifest.variants || [],
            dependencies: [] // Could analyze imports to find dependencies
          });
        } catch {
          // No manifest or invalid
        }
      }
    } catch (error) {
      console.warn('Failed to gather sections:', error);
    }

    return sections;
  }

  /**
   * Gather view information from DSL files
   */
  private async gatherViews(): Promise<ViewContext[]> {
    const views: ViewContext[] = [];

    try {
      // Check for views in packages/dsl/examples
      const viewsPath = path.join(this.projectPath, '../../dsl/examples');
      const viewFiles = await fs.readdir(viewsPath);

      for (const file of viewFiles) {
        if (file.endsWith('.view.json')) {
          try {
            const viewPath = path.join(viewsPath, file);
            const view = JSON.parse(await fs.readFile(viewPath, 'utf-8'));
            
            views.push({
              id: view.meta.id,
              name: view.meta.name,
              layout: view.layout,
              sections: view.sections
            });
          } catch {
            // Invalid view file
          }
        }
      }
    } catch (error) {
      console.warn('Failed to gather views:', error);
    }

    return views;
  }

  /**
   * Analyze project structure
   */
  private async analyzeProjectStructure(): Promise<ProjectStructure> {
    const structure: ProjectStructure = {
      hasTokens: false,
      hasComponents: false,
      hasSections: false,
      hasViews: false,
      hasStorybook: false,
      componentCount: 0,
      sectionCount: 0
    };

    try {
      // Check for tokens
      try {
        await fs.access(path.join(this.projectPath, 'src/tokens'));
        structure.hasTokens = true;
      } catch {}

      // Check for components
      try {
        const componentsPath = path.join(this.projectPath, 'src/components');
        const components = await fs.readdir(componentsPath);
        structure.hasComponents = true;
        structure.componentCount = components.filter(f => f.endsWith('.tsx')).length;
      } catch {}

      // Check for Storybook
      try {
        const packageJson = JSON.parse(await fs.readFile(path.join(this.projectPath, 'package.json'), 'utf-8'));
        structure.hasStorybook = !!packageJson.devDependencies?.storybook;
      } catch {}

      // Check for sections
      try {
        await fs.access(path.join(this.projectPath, '../../sections'));
        structure.hasSections = true;
        
        const sections = await fs.readdir(path.join(this.projectPath, '../../sections'));
        structure.sectionCount = sections.filter(d => !d.includes('.')).length;
      } catch {}

      // Check for views
      try {
        await fs.access(path.join(this.projectPath, '../../dsl/examples'));
        structure.hasViews = true;
      } catch {}

    } catch (error) {
      console.warn('Failed to analyze project structure:', error);
    }

    return structure;
  }

  // Helper methods for parsing
  private parseTokenFile(content: string): Record<string, any> {
    // Simplified token parsing - would need more sophisticated parsing for real use
    const tokens: Record<string, any> = {};
    
    const exportMatch = content.match(/export\\s+const\\s+\\w+\\s*=\\s*(\\{[^}]+\\})/);
    if (exportMatch) {
      try {
        // This is a very simplified approach - real implementation would need proper AST parsing
        const tokenStr = exportMatch[1].replace(/"/g, '"').replace(/'/g, '"');
        return JSON.parse(tokenStr);
      } catch {
        // Fallback to regex extraction
      }
    }

    return tokens;
  }

  private parsePropsInterface(propsContent: string): Record<string, any> {
    const props: Record<string, any> = {};
    
    // Simple prop extraction (would need proper TypeScript AST parsing for real use)
    const propMatches = propsContent.match(/(\\w+)\\??:\\s*([^;,\\n]+)/g);
    if (propMatches) {
      propMatches.forEach(match => {
        const [, name, type] = match.match(/(\\w+)\\??:\\s*(.+)/) || [];
        if (name && type) {
          props[name] = { type: type.trim(), optional: match.includes('?') };
        }
      });
    }

    return props;
  }

  private extractVariants(content: string): string[] {
    const variants: string[] = [];
    
    // Look for variant props
    const variantMatch = content.match(/variant\\??:\\s*["']([^"']+)["']\\s*\\|\\s*["']([^"']+)["']/);
    if (variantMatch) {
      variants.push(...variantMatch.slice(1));
    }

    // Look for CSS classes
    const classMatches = content.match(/btn--([a-zA-Z]+)/g);
    if (classMatches) {
      classMatches.forEach(match => {
        const variant = match.replace('btn--', '');
        if (!variants.includes(variant)) {
          variants.push(variant);
        }
      });
    }

    return variants;
  }

  private analyzeCSS(content: string): { classes: string[], tokenUsage: string[] } {
    const classes: string[] = [];
    const tokenUsage: string[] = [];

    // Extract CSS classes
    const classMatches = content.match(/\\.(\\w+[\\w-]*)/g);
    if (classMatches) {
      classes.push(...classMatches.map(match => match.slice(1)));
    }

    // Extract token usage
    const tokenMatches = content.match(/var\\(--([\\w-]+)\\)/g);
    if (tokenMatches) {
      tokenUsage.push(...tokenMatches.map(match => match.slice(6, -1)));
    }

    return { classes, tokenUsage };
  }

  private analyzeStories(content: string): { stories: string[], variants: string[] } {
    const stories: string[] = [];
    const variants: string[] = [];

    // Extract story names
    const storyMatches = content.match(/export\\s+const\\s+(\\w+)\\s*:/g);
    if (storyMatches) {
      stories.push(...storyMatches.map(match => match.match(/(\\w+)/)![1]));
    }

    return { stories, variants: stories }; // Stories often represent variants
  }

  private calculateContrastRatio(tokens: string[]): string {
    // Simplified contrast calculation based on common tokens
    if (tokens.some(t => t.includes('primary') && t.includes('500'))) {
      return '4.5:1'; // Assuming primary colors meet contrast requirements
    }
    return 'unknown';
  }
}